import { memo, useEffect, useState, useCallback } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import { useAlert } from "@hl/base-components/lib/alert/useAlert";
import { NotFound } from "@hl/shared-features/lib/features/layout/NotFound";
import { ServerError } from "@hl/shared-features/lib/features/layout/ServerError";
import { logError } from "@hl/shared-features/lib/services/logger";
import { useParams } from "react-router-dom";

import { SectionType } from "apollo/graphql.generated";
import { TOOLS_URL } from "config";
import {
  mapPageThemeToStateTheme,
  mapStateToPageTheme,
} from "styles/theme/themeForPage";

import { processEditorState } from "../Editor/logic";
import ManagePage from "../ManagePage";
import {
  setContent,
  setTheme,
  pageVar,
  filesForUploadVar,
  setGate,
  clearPage,
} from "../ManagePage/state";
import {
  usePageUpdateMutation,
  usePageQuery,
} from "../queries/page.graphql.generated";

import { usePageUpload } from "./logic";

const EditPage = () => {
  const { pageId } = useParams();
  const [isReady, setReady] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [isDataCorrupted, setCorruptedData] = useState(false);
  const alert = useAlert();

  const { data, loading, error } = usePageQuery({
    fetchPolicy: "network-only",
    variables: {
      id: pageId ?? "",
    },
  });
  const page = data?.page;

  useEffect(() => {
    if (loading || !page) {
      return;
    }
    if (!page.sections.length) {
      setCorruptedData(true);
      return;
    }
    setContent(
      page.sections[0].content.editorContent
        ? page.sections[0].content.editorContent.state
        : ""
    );
    pageVar({
      ...pageVar(),
      id: page.id,
      name: page.name,
      slug: page.slug,
    });
    if (page.theme) {
      setTheme(mapPageThemeToStateTheme(page.theme));
    }
    if (page.gate) {
      setGate(page.gate);
    } else {
      setGate(null);
    }
    setReady(true);
  }, [loading, page, setReady]);

  useEffect(() => {
    return () => {
      clearPage();
    };
  }, []);

  const [pageUpdate, { loading: pageUpdateLoading }] = usePageUpdateMutation();

  const { getPageUploadUrlData } = usePageUpload({
    setErrorMessage: logError,
  });

  const handleSave = useCallback(async () => {
    try {
      setUploading(true);
      const pageData = pageVar();
      const filesForUpload = filesForUploadVar();
      const sections = await Promise.all(
        pageData.sections.map(async (section) => {
          const processedContent = JSON.stringify(
            await processEditorState(
              JSON.parse(
                section.content.editorContent
                  ? section.content.editorContent.state
                  : ""
              ),
              filesForUpload,
              getPageUploadUrlData
            )
          );

          return {
            id: "main",
            content: {
              editorContent: {
                state: processedContent,
              },
            },
            type: SectionType.EditorData,
          };
        })
      );

      if (!sections.length) {
        throw new Error("sections is empty");
      }

      await pageUpdate({
        variables: {
          pageId: pageId ?? "",
          input: {
            sections,
            name: pageData.name,
            theme: mapStateToPageTheme(pageData.theme),
            gateId: pageData.gate?.id || null,
          },
        },
      });
      alert.success("Page updated.");
    } catch (err) {
      alert.error("Error updating page. Please try again later.");
      logError(err);
    } finally {
      setUploading(false);
    }
  }, [page]);

  if (error) {
    if (error.graphQLErrors[0]?.extensions.status == 403) {
      return <NotFound redirectUrl={TOOLS_URL.pages.list} />;
    }
    return <ServerError redirectUrl={TOOLS_URL.pages.list} />;
  }

  if (!pageId) {
    return <NotFound redirectUrl={TOOLS_URL.pages.list} />;
  }

  if (!isReady) {
    return <LoadingPage />;
  }

  if (isDataCorrupted) {
    return <ServerError redirectUrl={TOOLS_URL.pages.list} />;
  }

  if (!page) {
    return <NotFound redirectUrl={TOOLS_URL.pages.list} />;
  }

  return (
    <ManagePage
      editMode
      onSave={handleSave}
      isUpdating={isUploading || pageUpdateLoading}
    />
  );
};

export default memo(EditPage);
